'use client';

import { c as _c } from "react/compiler-runtime";
import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useLocale, useTranslations } from 'next-intl';
import { type JSX, useRef } from 'react';
import { PaintedMarketShareLeaf } from '@/components/chart/painted-market-share-display';
import { OutcomeCard } from '@/components/markets/outcome-card';
import { Panel, PanelContent } from '@/components/ui/panel';
import { resolveProposalCategory } from '@/config/proposal-display';
import { D9_SCALE } from '@/config/solana';
import type { PaintedMarketShareSnapshot } from '@/lib/chart/painted-market-share-display';
import { formatPercentValue } from '@/lib/format/format-number';
import { MOTION_POSITION } from '@/lib/ui/easing';
const FALLBACK_EMOJI = '\u{1F4A1}';
export interface MarketOutcomeOverviewRow {
  index: number;
  label: string;
  /**
   * Lifecycle-correct SDK spot price (D9). Null means fresh executable pool
   * state is unavailable; conviction may still drive the displayed percent.
   */
  priceD9: string | null;
  /**
   * Raw per-outcome conviction as a 0→>1 fraction (same as
   * `useOutcomeConviction` / consensus chart legend). When set, this wins over
   * `priceD9` so the row matches the chart. Can exceed 1.0 (e.g. 1.277 =
   * 127.7%) — never normalised to a cross-outcome share.
   */
  conviction?: number | undefined;
  imageUrl?: string | undefined;
  imageNsfw: boolean;
}
export interface MarketOutcomesOverviewPanelProps {
  marketPubkey: string;
  marketCategory: string | null;
  /** Consensus threshold shown as the goal percentage beside each outcome. */
  goalPercent?: number | undefined;
  /** Outcomes in any order; rendered by descending displayed share (ties: lower `index` first). */
  rows: MarketOutcomeOverviewRow[];
  /** Explicit chart selections. Empty leaves every outcome unticked and shows all lines. */
  selectedOutcomeIndices: readonly number[];
  onOutcomeChange: (index: number) => void;
  readonly paintedDisplay?: {
    readonly outcomeOrder: ReadonlyArray<number>;
  };
}
function priceToImpliedPercent(priceD9: string | null): number | null {
  if (priceD9 === null || !/^\d+$/.test(priceD9)) return null;
  const n = Number(priceD9);
  if (!Number.isFinite(n)) return null;
  return n / D9_SCALE * 100;
}
function overviewPercent(row: MarketOutcomeOverviewRow): number | null {
  const conviction = row.conviction;
  if (conviction !== undefined && Number.isFinite(conviction) && conviction >= 0) {
    return conviction * 100;
  }
  return priceToImpliedPercent(row.priceD9);
}

/**
 * Outcomes strip above trade tabs (Figma 3467:281 / 3478:659). Tokens: Panel
 * `density="compact"` (PanelContent horizontal inset 16px); title↔grid
 * `gap-4` (16px); grid `gap-2` (8px); chip `p-2` + inner `gap-2`; thumb 40×40
 * thumb `rounded-item` (4px); chip `rounded-overlay`; selected hairline `border-outcome-*-text`.
 */
export function MarketOutcomesOverviewPanel({
  marketPubkey,
  marketCategory,
  goalPercent,
  rows,
  selectedOutcomeIndices,
  onOutcomeChange,
  paintedDisplay
}: MarketOutcomesOverviewPanelProps): JSX.Element | null {
  const t = useTranslations('market');
  const locale = useLocale();
  const reduceMotion = useReducedMotion();
  const selectedOutcomes = new Set(selectedOutcomeIndices);
  const paintedOrder = new Map(paintedDisplay?.outcomeOrder.map((outcomeIndex, position) => [outcomeIndex, position]) ?? []);
  const displayRows: MarketOutcomeOverviewRow[] = rows.toSorted((a, b) => {
    // Before the first painted frame `outcomeOrder` is empty; the server-rendered
    // conviction order holds until the painted store takes over.
    if (paintedOrder.size > 0) {
      return (paintedOrder.get(a.index) ?? Number.MAX_SAFE_INTEGER) - (paintedOrder.get(b.index) ?? Number.MAX_SAFE_INTEGER) || a.index - b.index;
    }
    const pb = overviewPercent(b) ?? Number.NEGATIVE_INFINITY;
    const pa = overviewPercent(a) ?? Number.NEGATIVE_INFINITY;
    if (pb !== pa) {
      return pb - pa;
    }
    return a.index - b.index;
  });
  const categoryEmoji: string = marketCategory !== null ? resolveProposalCategory(marketCategory)?.emoji ?? FALLBACK_EMOJI : FALLBACK_EMOJI;
  const unavailableText = t('backers.earnings_unavailable');
  if (displayRows.length === 0) {
    return null;
  }
  return <Panel density="compact" className="min-w-0">
      <PanelContent className="flex min-w-0 flex-col gap-4">
        <span className="text-body-lg font-semibold leading-5 tracking-tight text-foreground-primary">
          {t('outcomes_overview_title')}
        </span>
        <LazyMotion features={domAnimation}>
          <ul className="m-0 grid min-w-0 list-none grid-cols-2 gap-2 p-0 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4">
            {displayRows.map(row => {
            const pctValue = overviewPercent(row);
            if (paintedDisplay !== undefined) {
              return <PaintedOutcomeOverviewCard key={row.index} row={row} serverPercent={pctValue} goalPercent={goalPercent} marketPubkey={marketPubkey} categoryEmoji={categoryEmoji} selected={selectedOutcomes.has(row.index)} onOutcomeChange={onOutcomeChange} locale={locale} ariaUnavailable={t('outcomes_overview_card_aria_unavailable', {
                outcome: row.label
              })} unavailableText={unavailableText} ariaLabel={(percent: string) => t('outcomes_overview_card_aria', {
                outcome: row.label,
                percent
              })} reduceMotion={reduceMotion === true} />;
            }
            return <m.li key={row.index} layout={reduceMotion === true ? false : 'position'} transition={reduceMotion === true ? {
              duration: 0
            } : MOTION_POSITION} className="min-w-0">
                  <OutcomeCard variant="interactive" entityPubkey={marketPubkey} index={row.index} label={row.label} percent={pctValue ?? 0} goalPercent={goalPercent} showPercent={pctValue !== null} decimals imageUrl={row.imageUrl ?? null} imageNsfw={row.imageNsfw} imageAlt={row.label} fallbackEmoji={categoryEmoji} isSelected={selectedOutcomes.has(row.index)} onSelect={onOutcomeChange} ariaLabel={pctValue === null ? t('outcomes_overview_card_aria_unavailable', {
                outcome: row.label
              }) : t('outcomes_overview_card_aria', {
                outcome: row.label,
                percent: `${formatPercentValue(pctValue, locale)}%`
              })} />
                </m.li>;
          })}
          </ul>
        </LazyMotion>
      </PanelContent>
    </Panel>;
}
function PaintedOutcomeOverviewCard(t0) {
  const $ = _c(40);
  const {
    row,
    serverPercent,
    goalPercent,
    marketPubkey,
    categoryEmoji,
    selected,
    onOutcomeChange,
    locale,
    ariaUnavailable,
    unavailableText,
    ariaLabel,
    reduceMotion
  } = t0;
  const buttonRef = useRef(null);
  let t1;
  if ($[0] !== locale || $[1] !== serverPercent || $[2] !== unavailableText) {
    t1 = serverPercent === null ? unavailableText : `${formatPercentValue(serverPercent, locale)}%`;
    $[0] = locale;
    $[1] = serverPercent;
    $[2] = unavailableText;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const serverText = t1;
  let t2;
  if ($[4] !== ariaLabel || $[5] !== ariaUnavailable || $[6] !== serverPercent || $[7] !== serverText) {
    t2 = serverPercent === null ? ariaUnavailable : ariaLabel(serverText);
    $[4] = ariaLabel;
    $[5] = ariaUnavailable;
    $[6] = serverPercent;
    $[7] = serverText;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const serverAria = t2;
  let t3;
  if ($[9] !== goalPercent) {
    t3 = goalPercent === undefined ? "" : `/${String(Math.round(goalPercent))}%`;
    $[9] = goalPercent;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const serverGoalText = t3;
  const t4 = reduceMotion ? false : "position";
  let t5;
  if ($[11] !== reduceMotion) {
    t5 = reduceMotion ? {
      duration: 0
    } : MOTION_POSITION;
    $[11] = reduceMotion;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = row.imageUrl ?? null;
  let t7;
  if ($[13] !== ariaLabel || $[14] !== ariaUnavailable || $[15] !== locale || $[16] !== row.index || $[17] !== unavailableText) {
    t7 = snapshot => {
      if (snapshot.availability === "error") {
        return {
          text: unavailableText,
          accessibilityLabel: `${ariaUnavailable} — ${unavailableText}`
        };
      }
      const value = snapshot.values[row.index];
      const available = typeof value === "number" && Number.isFinite(value);
      const text = available ? `${formatPercentValue(value * 100, locale)}%` : unavailableText;
      return {
        text,
        accessibilityLabel: available ? ariaLabel(text) : `${ariaUnavailable} — ${unavailableText}`
      };
    };
    $[13] = ariaLabel;
    $[14] = ariaUnavailable;
    $[15] = locale;
    $[16] = row.index;
    $[17] = unavailableText;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== serverText || $[20] !== t7) {
    t8 = <PaintedMarketShareLeaf fallback={serverText} accessibilityTargetRef={buttonRef} className="shrink-0 text-body-lg font-semibold tabular-nums tracking-tight" format={t7} />;
    $[19] = serverText;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== serverGoalText) {
    t9 = <PaintedMarketShareLeaf fallback={serverGoalText} className="text-caption font-semibold tabular-nums" format={_temp2} />;
    $[22] = serverGoalText;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== categoryEmoji || $[25] !== marketPubkey || $[26] !== onOutcomeChange || $[27] !== row.imageNsfw || $[28] !== row.index || $[29] !== row.label || $[30] !== selected || $[31] !== serverAria || $[32] !== t6 || $[33] !== t8 || $[34] !== t9) {
    t10 = <OutcomeCard variant="interactive" entityPubkey={marketPubkey} index={row.index} label={row.label} percent={0} showPercent={true} decimals={true} imageUrl={t6} imageNsfw={row.imageNsfw} imageAlt={row.label} fallbackEmoji={categoryEmoji} isSelected={selected} onSelect={onOutcomeChange} ariaLabel={serverAria} selectionControlRef={buttonRef} percentContent={t8} goalContent={t9} />;
    $[24] = categoryEmoji;
    $[25] = marketPubkey;
    $[26] = onOutcomeChange;
    $[27] = row.imageNsfw;
    $[28] = row.index;
    $[29] = row.label;
    $[30] = selected;
    $[31] = serverAria;
    $[32] = t6;
    $[33] = t8;
    $[34] = t9;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== t10 || $[37] !== t4 || $[38] !== t5) {
    t11 = <m.li layout={t4} transition={t5} className="min-w-0">{t10}</m.li>;
    $[36] = t10;
    $[37] = t4;
    $[38] = t5;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  return t11;
}
function _temp2(snapshot_0) {
  return {
    text: snapshot_0.availability === "error" || snapshot_0.threshold === null ? "" : `/${String(Math.round(snapshot_0.threshold * 100))}%`
  };
}